// A centred yes/no question over a dimmed screen, styled like an iOS system
// alert: title, explanation, then Cancel | Confirm side by side (#390). Used in
// place of window.confirm(), which iOS titles with the site's address.
import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { cn } from "@higgins/utils"
import { DialogOverlay, DialogPortal } from "./dialog"

export interface ConfirmDialogProps {
  open: boolean
  /** Called with false on Cancel or Escape — never while `busy`. */
  onOpenChange: (open: boolean) => void
  title: React.ReactNode
  description: React.ReactNode
  confirmLabel: string
  cancelLabel?: string
  /** Shown on the confirm button while `busy`, e.g. "Removing…". */
  busyLabel?: string
  /** Red confirm button, for an action that removes something. */
  destructive?: boolean
  /** The action is under way: both buttons are disabled and the dialog stays open. */
  busy?: boolean
  onConfirm: () => void
  /**
   * Where focus goes when the dialog closes — the button that opened it. A tap
   * on the iPad doesn't focus a button, so what had focus is no guide there.
   */
  returnFocus?: React.RefObject<HTMLElement | null>
}

const ACTION = "min-h-11 flex-1 px-3 text-[17px] transition-colors active:bg-slate-200/70 disabled:opacity-50 focus-visible:outline-none focus-visible:bg-slate-200/70"

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel = "Cancel",
  busyLabel,
  destructive = false,
  busy = false,
  onConfirm,
  returnFocus,
}: ConfirmDialogProps) {
  // Radix hands focus back only to a Dialog.Trigger; this dialog is opened by
  // the caller's own button, so return focus to it (`returnFocus`), else to
  // whatever had focus when it opened.
  const opener = React.useRef<HTMLElement | null>(null)
  React.useLayoutEffect(() => {
    if (open) opener.current = document.activeElement as HTMLElement | null
  }, [open])
  const stayOpenWhileBusy = (e: Event) => {
    if (busy) e.preventDefault()
  }
  return (
    <DialogPrimitive.Root open={open} onOpenChange={(next) => !busy && onOpenChange(next)}>
      <DialogPortal>
        <DialogOverlay className="bg-black/40" />
        <DialogPrimitive.Content
          role="alertdialog"
          onEscapeKeyDown={stayOpenWhileBusy}
          onCloseAutoFocus={(e) => {
            e.preventDefault()
            ;(returnFocus?.current ?? opener.current)?.focus()
          }}
          // An iOS alert is answered, not tapped away.
          onInteractOutside={(e) => e.preventDefault()}
          className="fixed left-1/2 top-1/2 z-50 w-[min(18rem,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-2xl bg-white/95 text-center text-slate-900 shadow-xl backdrop-blur transition-all duration-200 data-[state=open]:scale-100 data-[state=open]:opacity-100 data-[state=closed]:scale-95 data-[state=closed]:opacity-0"
        >
          <div className="px-4 pb-4 pt-5">
            <DialogPrimitive.Title className="text-[17px] font-semibold leading-snug">{title}</DialogPrimitive.Title>
            <DialogPrimitive.Description className="mt-1 text-[13px] leading-snug text-slate-700">{description}</DialogPrimitive.Description>
          </div>
          <div className="flex divide-x divide-slate-300 border-t border-slate-300">
            <DialogPrimitive.Close className={cn(ACTION, "text-sky-600")} disabled={busy}>
              {cancelLabel}
            </DialogPrimitive.Close>
            <button
              type="button"
              onClick={onConfirm}
              disabled={busy}
              className={cn(ACTION, "font-semibold", destructive ? "text-red-600" : "text-sky-600")}
            >
              {busy && busyLabel ? busyLabel : confirmLabel}
            </button>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </DialogPrimitive.Root>
  )
}
